import { api } from "./api";

export const HOST_ONBOARDING_STORAGE_KEY = "rsvp.hostOnboarding";
export const HOST_ONBOARDING_RETURN_PATH = "/onboarding?step=loop";
export const HOST_ONBOARDING_RESUME_PATH = "/onboarding?resume=1";

const STEPS = ["gate", "profile", "loop", "school", "payment", "theme"];

const EMPTY_SESSION = {
  step: "gate",
  schoolSlug: null,
  activated: false,
  hostIntent: false,
};

export function isHostOnboardingStep(value) {
  return STEPS.includes(value);
}

export function accountFinishedHostOnboarding(account) {
  return Boolean(account?.onboarding_completed_at);
}

/** Show resume nudge when a signed-in host left setup unfinished. */
export function shouldShowHostOnboardingResume(account) {
  return Boolean(account) && !accountFinishedHostOnboarding(account);
}

/**
 * Pick the best step when returning via resume banner.
 * Skips the gate once they've already opted into hosting (or have a name).
 */
export function resumeHostOnboardingStep(account, session = readHostOnboardingSession()) {
  if (session?.step && session.step !== "gate") return session.step;
  if (session?.hostIntent || account?.name) {
    return account?.name ? "loop" : "profile";
  }
  return "gate";
}

/**
 * Branch-aware progress. Event-first path is 3 steps; school branch adds school + payment.
 */
export function hostOnboardingProgress(step, { schoolBranch = false } = {}) {
  if (step === "gate") {
    return { label: "Welcome", current: 0, max: schoolBranch ? 5 : 3 };
  }

  if (schoolBranch) {
    const map = {
      profile: { label: "Profile", current: 1 },
      loop: { label: "Go live", current: 2 },
      school: { label: "School", current: 3 },
      payment: { label: "Payments", current: 4 },
      theme: { label: "Theme", current: 5 },
    };
    const meta = map[step] || { label: "", current: 0 };
    return { ...meta, max: 5 };
  }

  const map = {
    profile: { label: "Profile", current: 1 },
    loop: { label: "Go live", current: 2 },
    theme: { label: "Theme", current: 3 },
    // School/payment shouldn't appear on the short path, but stay sane if they do.
    school: { label: "School", current: 2 },
    payment: { label: "Payments", current: 2 },
  };
  const meta = map[step] || { label: "", current: 0 };
  return { ...meta, max: 3 };
}

export async function completeHostOnboarding(updateAccount) {
  const { data } = await api.patch("/organizers/me", { onboarding_completed: true });
  const organizer = data?.organizer;
  if (organizer && updateAccount) updateAccount(organizer);
  clearHostOnboardingSession();
  return organizer;
}

/**
 * Leave the wizard without marking onboarding complete — feed can resume later.
 */
export function deferHostOnboarding(patch = {}) {
  writeHostOnboardingSession({
    hostIntent: true,
    ...patch,
  });
}

export function readHostOnboardingSession() {
  try {
    const raw = sessionStorage.getItem(HOST_ONBOARDING_STORAGE_KEY);
    if (!raw) return { ...EMPTY_SESSION };
    const parsed = JSON.parse(raw);
    return {
      step: isHostOnboardingStep(parsed.step) ? parsed.step : "gate",
      schoolSlug: typeof parsed.schoolSlug === "string" && parsed.schoolSlug ? parsed.schoolSlug : null,
      activated: Boolean(parsed.activated),
      hostIntent: Boolean(parsed.hostIntent),
    };
  } catch {
    return { ...EMPTY_SESSION };
  }
}

export function writeHostOnboardingSession(patch) {
  const prev = readHostOnboardingSession();
  const next = {
    step: isHostOnboardingStep(patch.step) ? patch.step : prev.step,
    schoolSlug:
      patch.schoolSlug !== undefined
        ? (typeof patch.schoolSlug === "string" && patch.schoolSlug ? patch.schoolSlug : null)
        : prev.schoolSlug,
    activated: patch.activated !== undefined ? Boolean(patch.activated) : prev.activated,
    hostIntent: patch.hostIntent !== undefined ? Boolean(patch.hostIntent) : prev.hostIntent,
  };
  try {
    sessionStorage.setItem(HOST_ONBOARDING_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function clearHostOnboardingSession() {
  try {
    sessionStorage.removeItem(HOST_ONBOARDING_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function createEventPathForOnboarding() {
  return `/create?onboarding=1`;
}

export function markHostOnboardingActivated(extra = {}) {
  return writeHostOnboardingSession({ activated: true, hostIntent: true, ...extra });
}
